import { QueryClient, useQuery } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { GeneratorCard } from '../../components/_molecules/GeneratorCard';
import { NewLandingPageFooter } from '../../components/_molecules/LandingPageFooter';
import NewAppLayout from '../../components/layouts/NewAppLayout';
import useRoutePrefetch from '../../hooks/useRoutePrefetch';
import useRouterMatch from '../../hooks/useRouterMatch';
import { GeneratorMinimumDTO } from '../../services/backendService/types';
import CoreAPIService from '../../services/CoreAPIService';
import { getGeneratorsUrl } from '../../services/DiscoverService/discoverUtils';
import { getBaseUrl } from '../../utils/client';
import { detectAndBestEffortSetLanguage } from '../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../utils/react-query-utils';
import { LicenseCoarseLevels } from '../../utils/tenantPlanUtils';

const PageContainer = styled.div<{ $isAuthenticated: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${theme.colors.background};

  @media (min-width: 800px) {
    padding-left: ${({ $isAuthenticated }) => ($isAuthenticated ? '240px' : '0px')};
  }
`;

const PageHead = () => {
  const { router } = useRouterMatch();

  const title = 'All customizable lessons - Curipod';
  const description =
    'Find every single customizable lesson on Curipod made by pedagogy experts.';
  return (
    <Head>
      <title>{title}</title>
      <meta property="og:title" content={title} key="title" />
      <meta name="twitter:title" content={title} />
      <meta name="description" content={description} key="namedescription" />
      <meta property="description" content={description} key="description" />
      <meta property="og:description" content={description} key="og:description" />
      <meta name="twitter:description" content={description} />
      <meta property="og:image" content={'/og_preview.png'} key="imageurl" />
      <meta property="og:image:secure_url" content={'/og_preview.png'} key="imageurl" />
      <meta property="og:type" content="website" />

      <meta property="og:url" content={`${getBaseUrl()}${router.asPath}`} key="ogurl" />
      <meta property="og:image:type" content={'image/svg+xml'} key="imagetype" />
      <meta property="og:image:alt" content={'The curipod logo'} key="imageAlt" />
      <meta property="og:image:width" content={'160'} key="imageWidth" />
      <meta property="og:image:height" content={'108'} key="imageHeight" />
      <meta name="twitter:image" content={'/og_preview.png'} />
      <meta name="twitter:site" content={'@curipodofficial'} />
      <meta name="twitter:card" content={'summary'} />
      <meta name="twitter:widgets:theme" content="light" />
      <meta name="twitter:url" content={`${getBaseUrl()}${router.asPath}`} />
    </Head>
  );
};

const AllCustomizableLessonsGrid = () => {
  const { data } = useQuery(
    ['customizable-lessons'],
    async () => {
      return Promise.resolve<GeneratorMinimumDTO[]>([]);
    },
    {
      staleTime: 1000 * 60 * 60,
    },
  );

  return (
    <div style={{ width: '100%', paddingTop: 60, minHeight: '70vh' }}>
      <ResponsiveGeneratorsGridContainer>
        {data &&
          data.map(generator => {
            return (
              <Link
                href={getGeneratorsUrl({
                  title: generator.title,
                  generatorId: generator.id,
                })}
                key={generator.id}
              >
                <a style={{ height: '100%', width: '100%' }}>
                  <GeneratorCard
                    subTitle=""
                    title={generator.title || ''}
                    coverImageId={generator.coverImageId}
                    status={undefined}
                    firstPublished={0}
                    isPremiumGenerator={
                      generator.generatorPlan === LicenseCoarseLevels.premium
                    }
                    generatorPlan={generator.generatorPlan}
                  />
                </a>
              </Link>
            );
          })}
      </ResponsiveGeneratorsGridContainer>
    </div>
  );
};

export const ResponsiveGeneratorsGridContainer = styled.div<{ $isCentered?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  padding: 20px;

  @media (max-width: 4096px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  }
  @media (max-width: 1440px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
  @media (max-width: 1280px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 950px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 800px) {
    // 800 we go into mobile screen mode
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }

  ${({ $isCentered }) =>
    $isCentered &&
    css`
      max-width: 500px;
      margin: 0 auto;
      @media (max-width: 4096px) {
        grid-template-columns: 1fr 1fr 1fr;
      }
      @media (max-width: 700px) {
        grid-template-columns: 1fr 1fr;
      }
    `}
`;

const CustomizableLessonsPage = () => {
  const session = useSession();
  useRoutePrefetch(`/generators/[generatorName]/g/[generatorId]`);
  return (
    <>
      <NewAppLayout allowUnAuthenticated={true}>
        <PageHead />
        <PageContainer $isAuthenticated={session.status === 'authenticated'}>
          <AllCustomizableLessonsGrid />
          <NewLandingPageFooter footerBackgroundColor={theme.colors.paleBeige} />
        </PageContainer>
      </NewAppLayout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );

  const queryClient = new QueryClient();

  await queryClient.prefetchQuery(
    ['customizable-lessons'],
    async () => {
      return CoreAPIService.fetchAllCustomizableLessons();
    },
    {
      staleTime: 1000 * 60 * 60,
    },
  );

  return {
    props: {
      dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
      ...(await detectAndBestEffortSetLanguage(context)),
    },
  };
};

export default CustomizableLessonsPage;
